// Masks credential values before anything is written to a console.
//
// Development logging is genuinely useful - seeing the real request body is
// how you confirm the client matches the API contract. But the same code path
// runs against a real backend, where the body holds a live API key and the
// response holds a bearer token. Printing those would defeat the rule the rest
// of this layer follows (see AuthManager, which never hands out a raw token).
//
// The compromise: keep every KEY and every non-credential VALUE intact, so the
// shape stays verifiable against the documented contract, and replace only the
// secret itself with a short prefix plus a length. That is enough to tell
// "the right key was sent" from "the wrong key was sent" without revealing it.
//
// The pattern deliberately matches sanitizeContext()'s, so a value considered
// sensitive in an error context is considered sensitive in a log line too.
const SENSITIVE_KEY =
  /auth|cookie|token|credential|password|secret|session[-_]?key|api[-_]?key|bearer/i;

// Keys that match the pattern above but carry no secret. `tokenType` holds the
// literal string "Bearer" and is part of the documented auth response, so
// masking it would destroy contract-verification value for no security gain.
const NEVER_SENSITIVE = new Set(['tokentype', 'authenticated', 'authorized']);

const MAX_DEPTH = 4;
const PREVIEW_CHARS = 4;

export function redact(value, depth = 0) {
  if (value === null || typeof value !== 'object') return value;
  if (depth >= MAX_DEPTH) return '[…]';

  if (Array.isArray(value)) {
    return value.map((entry) => redact(entry, depth + 1));
  }

  const output = {};
  for (const [key, entry] of Object.entries(value)) {
    const sensitive = SENSITIVE_KEY.test(key) && !NEVER_SENSITIVE.has(key.toLowerCase());
    output[key] = sensitive ? mask(entry) : redact(entry, depth + 1);
  }
  return output;
}

function mask(value) {
  if (typeof value !== 'string' || value.length === 0) return '[redacted]';
  // `Bearer <token>` - keep the scheme, which is part of the contract, and
  // mask only the credential after it.
  const [scheme, ...rest] = value.split(' ');
  if (rest.length > 0 && /^bearer$/i.test(scheme)) {
    return `${scheme} ${mask(rest.join(' '))}`;
  }
  return `${value.slice(0, PREVIEW_CHARS)}… [redacted, ${value.length} chars]`;
}
